/**
 * maskUtils
 * Utilitário para aplicar máscaras e validações em diferentes tipos de dados.
 */
export const maskUtils = {
  /**
   * Remove todos os caracteres não numéricos de uma string.
   *
   * @param value - A string de entrada que pode conter caracteres numéricos e não numéricos.
   * @returns Uma string contendo apenas números.
   */
  somenteNumeros(value: string) {
    return value ? value.replace(/\D/g, '') : '';
  },

  /**
   * Formata um número de celular para o formato (999)99999-9999 sem espaços.
   *
   * @param value - A string de entrada representando o número de celular.
   * @returns Uma string formatada como (999)99999-9999 ou uma string vazia se o valor for nulo ou indefinido.
   */
  celular(value: string) {
    if (!value) {
      return '';
    }
    return value
      .replace(/\D/g, '') // Remove caracteres não numéricos
      .replace(/^(\d{3})(\d)/, '($1)$2') // Adiciona parênteses nos três primeiros dígitos
      .replace(/(\d{5})(\d{1,4})/, '$1-$2') // Adiciona o hífen após os primeiros cinco dígitos
      .replace(/(-\d{4})\d+?$/, '$1'); // Limita o número total de dígitos a 11
  },

  /**
   * Formata um número de telefone para o formato (041) 999449784.
   *
   * @param value - A string de entrada representando o número de telefone.
   * @returns Uma string formatada como (041) 999449784 ou uma string vazia se o valor for nulo ou indefinido.
   */
  telefone(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return value
      .replace(/\D/g, '') // Remove caracteres não numéricos
      .replace(/^(\d{3})(\d)/, '($1) $2') // Adiciona parênteses nos três primeiros dígitos
      .replace(/(\d{5})(\d{4})/, '$1$2') // Formato direto sem hífen
      .replace(/(\d{9})\d+?$/, '$1'); // Limita o número total de dígitos a 12
  },

  /**
   * Formata um CPF para o formato 999.999.999-99.
   *
   * @param value - A string de entrada representando o CPF.
   * @returns Uma string formatada como 999.999.999-99.
   */
  cpf(value: string) {
    return value
      .replace(/\D/g, '') // Remove caracteres não numéricos
      .replace(/(\d{3})(\d)/, '$1.$2') // Adiciona o ponto após três dígitos
      .replace(/(\d{3})(\d)/, '$1.$2') // Adiciona o segundo ponto
      .replace(/(\d{3})(\d{1,2})/, '$1-$2') // Adiciona o hífen antes dos dois últimos dígitos
      .replace(/(-\d{2})\d+?$/, '$1'); // Limita o número total de dígitos a 11
  },

  /**
   * Formata um CNPJ para o formato 99.999.999/9999-99.
   *
   * @param value - A string de entrada representando o CNPJ.
   * @returns Uma string formatada como 99.999.999/9999-99.
   */
  cnpj(value: string) {
    return value
      .replace(/\D/g, '') // Remove caracteres não numéricos
      .replace(/(\d{2})(\d)/, '$1.$2') // Adiciona o ponto após os dois primeiros dígitos
      .replace(/(\d{3})(\d)/, '$1.$2') // Adiciona o segundo ponto
      .replace(/(\d{3})(\d)/, '$1/$2') // Adiciona a barra após os primeiros seis dígitos
      .replace(/(\d{4})(\d{1,2})/, '$1-$2') // Adiciona o hífen antes dos dois últimos dígitos
      .replace(/(-\d{2})\d+?$/, '$1'); // Limita o número total de dígitos a 14
  },

  /**
   * Formata um CEP para o formato 99999-999.
   *
   * @param value - A string de entrada representando o CEP.
   * @returns Uma string formatada como 99999-999.
   */
  cep(value: string) {
    return value
      .replace(/\D/g, '') // Remove caracteres não numéricos
      .replace(/^(\d{5})(\d{1,3})/, '$1-$2') // Adiciona o hífen depois dos primeiros cinco dígitos
      .replace(/(-\d{3})\d+?$/, '$1'); // Limita o número total de dígitos a 8
  },

  /**
   * Formata um RG para o formato 99.999.999-9.
   *
   * @param value - A string de entrada representando o RG.
   * @returns Uma string formatada como 99.999.999-9.
   */
  rg(value: string) {
    return value
      .replace(/\D/g, '') // Remove caracteres não numéricos
      .replace(/(\d{2})(\d)/, '$1.$2') // Adiciona o ponto após os dois primeiros dígitos
      .replace(/(\d{3})(\d)/, '$1.$2') // Adiciona o segundo ponto após os três dígitos
      .replace(/(\d{3})(\d)/, '$1-$2') // Adiciona o hífen antes do último dígito
      .replace(/(-\d)\d+?$/, '$1'); // Limita o número total de dígitos a 9
  },

  /**
   * Valida um e-mail com base em um padrão específico.
   *
   * @param value - A string de entrada representando o e-mail.
   * @returns A string de e-mail se for válida; caso contrário, retorna uma string vazia.
   */
  email(value: string) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(value) ? value : '';
  },
};
